import { useEffect, useMemo, useState } from 'react';
import {
    BrowserRouter,
    Route,
    Routes,
    useNavigate,
    Navigate,
    useParams,
    Outlet,
} from 'react-router-dom';

import { ThemeProvider, StyledEngineProvider, Box, Stack, Typography } from '@mui/material';
import { theme } from './theme';
import LoadingButton from '@mui/lab/LoadingButton';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import {
    // SelectPaymentTerms,
    AgreeToTerms,
    SelectFinalPaymentTerms,
} from '@lendica/select-paylater-terms';
import { PhoneAuth, ProtectedRoute, useAccountContext, invalidateToken } from '@lendica/auth';
import { matchBackend, matchHostname } from '@lendica/utils';

import { AppContainer, useAppContainerContext } from './AppContainer';
import { AppData, useAppDataContext, AuthContainer, useBillData } from './AppData';

import { StatusPage } from './components/StatusPage';
import { Fallback } from './Fallback';
import {
    CheckoutApproved,
    ShopifyPending,
    ShopifyRejected,
    ExceedsLimit,
    CheckoutRedirect,
} from './CheckoutStatuses';
import { ApplicationForm, OfferCalculator } from './ApplicationForm';
import { ConfirmBank } from './ConfirmBank';
import { SelectPaymentTermsView } from './views';
import { flag } from './lib/states';

import _ from 'lodash';

export const SelectFinalPaymentTermsView = ({ onTermSelected, onSelected }) => {
    const { billId, offerId } = useParams();
    const { bill, offers, isLoading } = useAppDataContext();

    useEffect(() => {
        scroll({
            top: 0,
        });
    }, []);

    return (
        !isLoading &&
        !!bill &&
        !!offers && (
            <SelectFinalPaymentTerms
                bill={bill}
                offers={offers}
                onTermSelected={offer => onTermSelected(billId, offer)}
                onSelected={offer => onSelected(billId, offer)}
            />
        )
    );
};

export const AgreeOnTermsView = ({ onGoBack, onAgree }) => {
    const { billId, offerId } = useParams();
    const { offers, loadOffers, createDeal, isLoading, setLoading } = useAppDataContext();
    const navigate = useNavigate();

    useEffect(() => {
        setLoading(true);
        if (!!offers) {
            setLoading(false);
        }
        // const getOffers = async () => {
        //     scroll({
        //         top: 0,
        //     });
        //     await loadOffers(billId);
        // };
        // getOffers();
    }, [offers]);

    const offer = useMemo(() => offers && offers?.find(item => item.id === offerId), [offers]);

    return (
        !isLoading &&
        (!!offer ? (
            <AgreeToTerms
                offer={offer}
                onGoBack={() => onGoBack(billId, offerId)}
                onAgree={async () => {
                    await createDeal(billId, offerId, onAgree);
                }}
            />
        ) : (
            <SelectFinalPaymentTermsView
                onSelected={(billId, offerId) => {
                    navigate(`/${billId}/agree-on-terms/${offerId}`);
                }}
                onTermSelected={(billId, offerId) => {
                    navigate(`/${billId}/select-terms/final/${offerId}`);
                }}
            />
        ))
    );
};

const Home = () => {
    return <Outlet />;
};

const AuthPages = () => {
    const { toggleFullscreen } = useAppContainerContext();
    const { billId } = useParams();
    const navigate = useNavigate();
    const { redirect } = useAppDataContext();

    const { storePortedTokens } = useAccountContext();

    useEffect(() => {
        toggleFullscreen();
        return () => toggleFullscreen();
    }, []);
    return (
        <PhoneAuth
            baseURL={matchBackend()}
            onSuccess={async tokens => {
                await storePortedTokens(tokens);
                setTimeout(() => {
                    navigate(!!redirect ? redirect : `/${billId}/bill`);
                }, 100);
            }}
        />
    );
};

const LoggedInAs = () => {
    const { billId, offerId } = useParams();
    const { addBill, getCompanyDetails, company } = useAppDataContext();
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    const { clearAccounts } = useAccountContext();

    useEffect(() => {
        (async () => {
            await getCompanyDetails(billId);
        })();
    }, []);

    // useEffect(() => {
    //     if (!!redirect && isLoading) {
    //         navigate(redirect);
    //         setIsLoading(false);
    //     }
    // }, [redirect]);

    const handleNext = async () => {
        setIsLoading(true);
        await addBill(billId, offerId);
    };

    const handleLogout = async () => {
        setIsLoading(true);
        try {
            await invalidateToken();
            await clearAccounts();
            navigate(`/${billId}/login`);
        } catch (e) {
            console.log(e);
        } finally {
            setIsLoading(false);
        }
    };
    return (
        !isLoading &&
        !!company &&
        !_.isEmpty(company) && (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-evenly',
                    alignItems: 'center',
                    height: '100%',
                }}
            >
                <Stack
                    direction="column"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    width={360}
                    spacing={2}
                >
                    <Typography variant="overline">Logged in as</Typography>
                    {/* <Avatar>{`${company?.first_name[0]}${company?.last_name[0]}`}</Avatar>
                    <Typography variant="body2">{`Company: ${company?.company_name}`}</Typography>
                    <Typography>{`Email: ${company?.email}`}</Typography> */}
                    <Typography color="text.secondary" variant="subtitle1">
                        Phone number
                    </Typography>
                    <Typography variant="h5">{`+1 (***) ***-${company?.phone_number?.slice(
                        -4
                    )}`}</Typography>
                </Stack>
                <Stack
                    direction="column"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    width={360}
                    spacing={2}
                >
                    <LoadingButton
                        loading={isLoading}
                        variant="contained"
                        color="primary"
                        fullWidth
                        sx={{ boxShadow: 'none' }}
                        onClick={handleNext}
                    >
                        Continue
                    </LoadingButton>
                    <LoadingButton
                        loading={isLoading}
                        variant="outlined"
                        color="primary"
                        fullWidth
                        onClick={handleLogout}
                    >
                        Use a different number
                    </LoadingButton>
                </Stack>
            </Box>
        )
    );
};

const AddBillView = () => {
    const { billId, offerId } = useParams();
    const { addBill } = useAppDataContext();

    useEffect(() => {
        (async () => {
            await addBill(billId, offerId);
        })();
    }, [billId, offerId]);

    return null;
    // return redirect && <Navigate to={redirect} />;
};

const Routing = () => {
    const navigate = useNavigate();
    const { interceptIfTokenExists, company, errorMsg } = useAppDataContext();
    const { bill } = useBillData();

    const selectPaymentTerms = (
        <SelectPaymentTermsView
            onSelected={(billId, offer) => {
                const res = interceptIfTokenExists();
                // console.log('trigger interceptIfTokenExists');
                if (res) {
                    // console.log('trigger navigate /profile');
                    return navigate(`/${billId}/profile/${offer?.id}`);
                }
                // console.log('if not res after interceptIfTokenExists navigate /bill');
                navigate(`/${billId}/bill/${offer?.id}`);
            }}
            onTermSelected={(billId, offer) => {
                navigate(`/${billId}/select-terms/preview/${offer?.id}`);
            }}
        />
    );

    const skipVendor = () => {
        return flag.allowSkipVendor(company);
    };

    const navigateToVendor = async (billId, offerId) => {
        // const bill = (await axios.get(`/company/bills?id=${billId}`)).data;
        // check if bank and vendor are filled
        if (flag.isCheckout(bill)) {
            navigate(`/${billId}/agree-on-terms/${offerId}`);
        } else {
            navigate(`/${billId}/confirm-bank/${offerId}`);
        }
    };

    const selectFinalPaymentTerms = (
        <SelectFinalPaymentTermsView
            onSelected={(billId, offer) => {
                navigateToVendor(billId, offer?.id);
            }}
            onTermSelected={(billId, offer) => {
                navigate(`/${billId}/select-terms/final/${offer?.id}`);
            }}
        />
    );

    return (
        <Routes>
            <Route path="/" element={<Home />}>
                <Route path="/partner" element={<Home />} />
                <Route path="/:billId">
                    <Route path="select-terms/preview" element={selectPaymentTerms} />
                    <Route path="select-terms/preview/:offerId" element={selectPaymentTerms} />
                    <Route element={<ProtectedRoute redirectPath={'login'} />}>
                        <Route path="profile" element={<LoggedInAs />} />
                        <Route path="profile/:offerId" element={<LoggedInAs />} />
                        <Route path="bill" element={<AddBillView />} />
                        <Route path="bill/:offerId" element={<AddBillView />} />
                        <Route path="select-terms/final" element={selectFinalPaymentTerms} />
                        <Route
                            path="select-terms/final/:offerId"
                            element={selectFinalPaymentTerms}
                        />
                        <Route
                            path="agree-on-terms/:offerId"
                            element={
                                <AgreeOnTermsView
                                    onGoBack={(billId, offerId) => {
                                        navigate(`/${billId}/select-terms/final/${offerId}`);
                                    }}
                                    onAgree={billId => {
                                        // navigate(`/${billId}/confirm-bank`);
                                        navigate(`/${billId}/approved`);
                                    }}
                                />
                            }
                        />
                        <Route
                            path="confirm-bank/:offerId"
                            element={<ConfirmBank skipVendor={skipVendor()} />}
                        />
                        <Route
                            path="declined"
                            element={
                                <StatusPage
                                    title="Your application is not approved this time"
                                    subtitle="You can track your payments and view reports in the portal."
                                    // duration={25}
                                    actionLabel="Go Now"
                                    action={() => {
                                        window?.location?.ancestorOrigins?.length > 0
                                            ? window
                                                  .open(
                                                      `https://portal.${matchHostname()}/`,
                                                      '_blank'
                                                  )
                                                  .focus()
                                            : (window.location = `https://portal.${matchHostname()}/`);
                                    }}
                                />
                            }
                        />
                        <Route
                            path="pending"
                            element={
                                <StatusPage
                                    title="Your application is being processed"
                                    subtitle="You can track your payments and view reports in the portal."
                                    // duration={20}
                                    actionLabel="Go Now"
                                    action={() => {
                                        window?.location?.ancestorOrigins?.length > 0
                                            ? window
                                                  .open(
                                                      `https://portal.${matchHostname()}/`,
                                                      '_blank'
                                                  )
                                                  .focus()
                                            : (window.location = `https://portal.${matchHostname()}/`);
                                    }}
                                />
                            }
                        />
                        <Route
                            path="submitted"
                            element={
                                <StatusPage
                                    title="This bill has already been submitted"
                                    subtitle="You can track your deal status in portal."
                                    duration={20}
                                    actionLabel="Go Now"
                                    action={() => {
                                        window?.location?.ancestorOrigins?.length > 0
                                            ? window
                                                  .open(
                                                      `https://portal.${matchHostname()}/`,
                                                      '_blank'
                                                  )
                                                  .focus()
                                            : (window.location = `https://portal.${matchHostname()}/`);
                                    }}
                                />
                            }
                        />
                        <Route path=":offerId/*" element={<ApplicationForm />} />
                        <Route path="approved" element={<CheckoutApproved />} />
                        <Route path="shop/pending" element={<ShopifyPending />} />
                        <Route path="shop/sorry" element={<ShopifyRejected />} />
                        <Route path="checkout/redirect" element={<CheckoutRedirect />} />
                    </Route>
                    <Route path="login/*" element={<AuthPages />} />
                </Route>
                <Route
                    path="/notfound"
                    element={
                        <StatusPage
                            title="Invalid Invoice"
                            subtitle="Looks like the link is outdated. Contact us if you need help."
                            duration={12}
                            actionLabel="Get Help"
                            action={() => {
                                location = 'https://www.golendica.com/#contact';
                            }}
                        />
                    }
                />
                <Route
                    path="/error"
                    element={
                        <StatusPage
                            title={errorMsg || 'Something Went Wrong.'}
                            subtitle="Looks like there's an error with your request. Contact us if you need help."
                            duration={20}
                            actionLabel="Get Help"
                            action={() => {
                                location = 'https://www.golendica.com/#contact';
                            }}
                        />
                    }
                />
                <Route path="/limit" element={<ExceedsLimit />} />
                <Route path="/home" element={<Fallback />} />
                <Route path="/offer-calculator" element={<OfferCalculator />} />
            </Route>
            <Route path="/*" element={<Navigate to="/home" />} />
        </Routes>
    );
};

export const App = () => {
    return (
        <BrowserRouter>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <StyledEngineProvider injectFirst>
                    <ThemeProvider theme={theme}>
                        <AuthContainer>
                            <AppData>
                                <AppContainer>
                                    <Routing />
                                </AppContainer>
                            </AppData>
                        </AuthContainer>
                    </ThemeProvider>
                </StyledEngineProvider>
            </LocalizationProvider>
        </BrowserRouter>
    );
};
